import { useMutation } from '@tanstack/react-query';
import {
  type Message,
  MessagingWhatsappMessagesService,
} from 'src/api/generated';
import { withClientMessageId } from '../../utils/optimistic-whatsapp-message.util';
import type {
  WhatsAppMessageMutation,
  WhatsAppMessageMutationOptions,
  WhatsAppMessageSubmission,
} from '../../types/whatsapp-message-mutation.types';

/**
 * Owns one neutral message mutation per submission, including its generated request.
 * @param options - Consumer-owned variables, scheduling and lifecycle effects.
 * @returns Request actions and latest observer state; no feature cache policy.
 */
export const useCreateWhatsAppMessage = <
  TSubmission extends WhatsAppMessageSubmission,
  TContext = unknown,
>(
  options: WhatsAppMessageMutationOptions<TSubmission, TContext>
): WhatsAppMessageMutation => {
  const { prepareSubmission, dispatch, ...lifecycle } = options;
  const mutation = useMutation({
    ...lifecycle,
    retry: false,
    mutationFn: (submission: TSubmission) => {
      const execute = async (signal?: AbortSignal): Promise<Message> => {
        signal?.throwIfAborted();
        const { optimisticData: _optimisticData, ...request } =
          submission.params;
        const pending = MessagingWhatsappMessagesService.createMessage(request);
        const cancel = () => pending.cancel();
        signal?.addEventListener('abort', cancel, { once: true });
        try {
          return await pending;
        } finally {
          signal?.removeEventListener('abort', cancel);
        }
      };
      return dispatch ? dispatch(execute, submission) : execute();
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    message: mutation.data,
    createWhatsAppMessage: (params) =>
      mutation.mutate(prepareSubmission(withClientMessageId(params))),
    createWhatsAppMessageAsync: (params) =>
      mutation.mutateAsync(prepareSubmission(withClientMessageId(params))),
  };
};
